// Active-Location resolution INSIDE one Business (Phase 2 / P2.8).
//
// Pure: it takes the authoritative accessible-Location list the server just
// returned for this Business (already narrowed to the member's EffectiveScope
// and to normally navigable rows), the URL, and whatever the browser
// remembered, and answers which Location — if any — is the active context.
// No I/O, no storage, no React: the whole matrix is testable and the order
// lives in exactly one place.
//
// The URL is a CLAIM, never authorization: an explicit `:locationId` that is
// not in the accessible set is an INVALID DEEP LINK and is reported as such —
// it is never silently replaced by the remembered Location, never rendered,
// and never fetched. Storage is a HINT: a remembered pair counts only when its
// Business is this Business and its Location is in the accessible set.
// Nothing here widens what the server offered.
import type { ActiveTenant } from './active-tenant';
import type { DiscoveredLocationLike } from './active-tenant-resolve';

export type LocationItem = DiscoveredLocationLike;

export type LocationResolution =
  /** the URL names an accessible Location: it is the context */
  | { kind: 'route'; location: LocationItem }
  /** the URL names a Location the member may not open here; `fallback` is where to send them (null = zero access) */
  | { kind: 'invalid-route'; requestedLocationId: string; fallback: LocationItem | null }
  /** no Location in the URL; the remembered pair is for this Business and still accessible */
  | { kind: 'saved'; location: LocationItem }
  /** no Location in the URL and nothing usable remembered: the deterministic first accessible Location */
  | { kind: 'fallback'; location: LocationItem }
  /** the member may open no Location of this Business right now */
  | { kind: 'none' };

export interface ResolveActiveLocationInput {
  businessId: string;
  /** the `:locationId` route param, if the URL carries one */
  routeLocationId: string | null | undefined;
  /** the remembered pair (`pb.active`), if any and however stale */
  saved: ActiveTenant | null;
  /** the server's accessible list for THIS Business, in canonical server order */
  accessible: readonly LocationItem[];
}

/** The canonical fallback: the Business's primary when accessible, else the first the server listed. */
export function preferredLocation(accessible: readonly LocationItem[]): LocationItem | null {
  return accessible.find((l) => l.primary) ?? accessible[0] ?? null;
}

export function resolveActiveLocation(input: ResolveActiveLocationInput): LocationResolution {
  const { accessible } = input;
  const byId = (id: string) => accessible.find((l) => l.id === id) ?? null;

  if (input.routeLocationId) {
    const hit = byId(input.routeLocationId);
    if (hit) return { kind: 'route', location: hit };
    // an explicit, inaccessible Location is NEVER folded into "absent"
    return { kind: 'invalid-route', requestedLocationId: input.routeLocationId, fallback: preferredLocation(accessible) };
  }

  if (input.saved && input.saved.businessId === input.businessId) {
    const hit = byId(input.saved.locationId);
    if (hit) return { kind: 'saved', location: hit };
  }

  const fallback = preferredLocation(accessible);
  if (fallback) return { kind: 'fallback', location: fallback };
  return { kind: 'none' };
}

/** The Location a resolution puts in context, or null (invalid route and zero access have none). */
export function activeLocationOf(r: LocationResolution): LocationItem | null {
  return r.kind === 'route' || r.kind === 'saved' || r.kind === 'fallback' ? r.location : null;
}
